/**
 * hulk-wishlist-cart-bridge.js  (v2)
 *
 * Narrow bridge for /apps/advanced-wishlist only.
 * Loaded only on that path via a Liquid gate in layout/theme.liquid.
 *
 * ── Problem A  "Move to cart" opens the drawer with stale contents ───────────
 *   v1 fired POST /cart/update.js with updates:{} immediately after the add
 *   response arrived.  That call races with Shopify's edge session and can
 *   return section HTML rendered against the pre-add cart.
 *
 *   Fix: after a confirmed /cart/add.js success, fetch fresh section HTML with
 *     GET /?sections=cart-drawer,cart-icon-bubble
 *   That endpoint renders the sections against whatever the current cart
 *   session contains at the moment it is processed.  Because the add response
 *   has already fully resolved before this fetch is fired, the add is
 *   guaranteed committed.  The response shape { "cart-drawer": "…html…",
 *   "cart-icon-bubble": "…html…" } matches exactly what renderContents() reads.
 *   The inner promise chain is now properly returned so it is not floating.
 *
 * ── Problem B  Remove / qty-change from the wishlist-page drawer errors ──────
 *   CartItems.updateQuantity (the method that ultimately runs for every drawer
 *   remove/qty-change) hard-codes sections_url: window.location.pathname.
 *   On /apps/advanced-wishlist that value is an app proxy path; Shopify cannot
 *   render theme sections from it and returns an error, which the catch() block
 *   displays as window.cartStrings.error.
 *
 *   v1 tried to fix this by patching CartDrawerItems.prototype.updateQuantity.
 *   That approach has a timing window: renderContents() can upgrade a fresh
 *   <cart-drawer-items> element before the prototype polling loop fires.
 *
 *   Fix: intercept at the fetch() layer instead.  Any POST to /cart/change on
 *   this page has its JSON body rewritten so sections_url becomes '/'.  This
 *   covers CartItems, CartDrawerItems, and Hulk's own cart buttons uniformly
 *   with no prototype patching and no timing dependency.
 *
 * ── Problem C  <cart-drawer-items> not re-hydrated after bridge DOM write ────
 *   When the bridge's fetch interceptor rewrites the drawer HTML after a
 *   remove/qty-change (via CartItems.updateQuantity), the fresh
 *   <cart-drawer-items> node is not upgraded.  The next click finds an
 *   unupgraded element.
 *
 *   Fix: after each successful DOM write from the bridge, call
 *   customElements.upgrade() on the new <cart-drawer-items> node, matching
 *   what CartDrawer.renderContents() already does in cart-drawer.js.
 *
 * ── Scope ─────────────────────────────────────────────────────────────────────
 *   Double-gated:
 *     1. layout/theme.liquid only emits the <script> tag when
 *        request.path contains '/apps/advanced-wishlist'
 *     2. The IIFE below returns immediately if the pathname check fails,
 *        so even if the file were somehow loaded on another page it does nothing
 */
(function () {
  'use strict';

  // ── Route guard ──────────────────────────────────────────────────────────────
  if (!window.location.pathname.startsWith('/apps/advanced-wishlist')) return;

  // ── Constants ────────────────────────────────────────────────────────────────
  var SAFE_SECTIONS_URL = '/';
  var DRAWER_SECTIONS   = 'cart-drawer,cart-icon-bubble';

  // ── Helper: upgrade <cart-drawer-items> inside a container after HTML swap ──
  // Matches what CartDrawer.renderContents() does in cart-drawer.js lines 107-110.
  function rehydrateCartDrawerItems(container) {
    if (!container) return;
    var cdi = container.querySelector('cart-drawer-items');
    if (cdi) customElements.upgrade(cdi);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Fix B + Fix C  — fetch() interceptor
  //
  // Intercepts two POST patterns on this page:
  //   1. /cart/add(.js)   — detect Hulk's move-to-cart, then open the drawer
  //                         with fresh section HTML (Fix A)
  //   2. /cart/change(.js) — rewrite sections_url in the body to '/' (Fix B)
  //
  // Everything else is passed through to _origFetch unchanged.
  // ═══════════════════════════════════════════════════════════════════════════
  var _origFetch = window.fetch;

  window.fetch = function (input, init) {
    var url    = (typeof input === 'string') ? input : ((input && input.url) || '');
    var method = ((init && init.method) || (input && input.method) || 'GET').toUpperCase();

    // ── Branch 1: /cart/add — intercept to trigger drawer open with fresh HTML
    if (method === 'POST' && url.indexOf('/cart/add') !== -1) {
      return _origFetch.apply(this, arguments).then(function (response) {
        if (!response.ok) return response;

        // Clone before Hulk's own .json() call consumes the body.
        var clonedForBridge = response.clone();

        // Return a promise so the inner chain is NOT floating.
        // The outer .then() returns the inner promise, which resolves to
        // `response` — exactly what Hulk's caller expects.
        return clonedForBridge.json().then(function (addedItem) {
          // If the response is an error shape (e.g. sold-out), skip.
          if (!addedItem || addedItem.status) return response;

          var cartDrawer = document.querySelector('cart-drawer');
          if (!cartDrawer || typeof cartDrawer.renderContents !== 'function') {
            return response;
          }

          // ── Fix A: fetch fresh section HTML from a valid theme route ─────
          // GET /?sections=cart-drawer,cart-icon-bubble  renders the sections
          // against the current (post-add) cart session.  No mutation, no race.
          return _origFetch('/?sections=' + DRAWER_SECTIONS, {
            method:  'GET',
            headers: { 'X-Requested-With': 'XMLHttpRequest' }
          })
            .then(function (r) { return r.json(); })
            .then(function (sectionsData) {
              // Build the shape renderContents() expects:
              //   parsedState.sections["cart-drawer"]    — section HTML string
              //   parsedState.sections["cart-icon-bubble"] — section HTML string
              //   parsedState.id                           — variant/product id
              var parsedState = {
                id:       addedItem.variant_id || addedItem.id,
                sections: sectionsData
              };
              // Remove is-empty from the <cart-drawer> element before rendering.
              // When the cart was empty at page load the element carries
              // class="drawer is-empty".  CSS rule:
              //   cart-drawer.is-empty .drawer__inner { display: grid; ... }
              // collapses the inner layout so item rows are hidden even though
              // the HTML is present.  renderContents() only checks .drawer__inner
              // for is-empty — it never removes it from the element itself.
              // product-form.js:142 uses this same one-line pattern for PDP.
              cartDrawer.classList.remove('is-empty');
              cartDrawer.renderContents(parsedState);
              // renderContents calls cartDrawer.open() internally (cart-drawer.js:115)
              // and already calls customElements.upgrade() on <cart-drawer-items>
              // (cart-drawer.js:107-110) — no extra work needed here.
            })
            .catch(function () {
              // Fallback: the add succeeded; just open the drawer even if the
              // section fetch failed.  Contents may be briefly stale but the
              // user can see the item after a reload.
              cartDrawer.open();
            })
            .then(function () {
              return response;
            });
        });
      });
    }

    // ── Branch 2: /cart/change — rewrite sections_url in the request body ───
    // Covers CartItems.updateQuantity, CartDrawerItems.updateQuantity, and any
    // cart-change request Hulk itself might fire — all in one place, with no
    // prototype patching required.
    if (method === 'POST' && url.indexOf('/cart/change') !== -1) {
      var rewrittenInit = _rewriteSectionsUrl(init, input);
      return _origFetch.call(this, input, rewrittenInit)
        .then(function (response) {
          return response.text().then(function (text) {
            // After a successful change, re-hydrate <cart-drawer-items> in the
            // updated drawer so the next remove/qty-change upgrade is in place.
            // (Fix C — covers the case where CartItems.updateQuantity is the
            // caller and replaces .drawer__inner innerHTML without upgrading.)
            try {
              JSON.parse(text); // validate response is JSON before touching DOM
              var drawerInner = document.querySelector('#CartDrawer .drawer__inner')
                             || document.getElementById('CartDrawer-CartItems');
              rehydrateCartDrawerItems(drawerInner ? drawerInner.closest('#CartDrawer') : null);
            } catch (e) { /* non-JSON response — skip re-hydration */ }

            // Reconstruct a Response so the rest of the caller chain
            // (.then(r => r.text()) in CartItems.updateQuantity) still works.
            return new Response(text, {
              status:  response.status,
              headers: response.headers
            });
          });
        });
    }

    // ── All other fetches: pass through untouched ────────────────────────────
    return _origFetch.apply(this, arguments);
  };

  // ── Helper: clone an init object and rewrite sections_url in the JSON body ─
  function _rewriteSectionsUrl(init, input) {
    // Determine the raw body string.
    // init.body is set by CartItems.updateQuantity (a JSON string).
    // input may be a Request object if the caller used new Request(...).
    var rawBody = (init && init.body) || (input && typeof input !== 'string' && input.body) || null;

    if (!rawBody || typeof rawBody !== 'string') {
      // Body is not a plain JSON string (e.g. FormData, blob) — leave unchanged.
      return init;
    }

    var parsed;
    try {
      parsed = JSON.parse(rawBody);
    } catch (e) {
      // Not JSON — leave unchanged.
      return init;
    }

    // Only rewrite if sections_url is present and currently points to the app proxy.
    // Using a broad guard (any non-'/' value while on this page) is safe here
    // because we are already inside the wishlist-page-only route guard.
    if (!parsed.sections_url || parsed.sections_url === SAFE_SECTIONS_URL) {
      return init;
    }

    parsed.sections_url = SAFE_SECTIONS_URL;

    // Shallow-clone init to avoid mutating the caller's object.
    var newInit = {};
    if (init) {
      for (var key in init) {
        if (Object.prototype.hasOwnProperty.call(init, key)) newInit[key] = init[key];
      }
    }
    newInit.body = JSON.stringify(parsed);
    return newInit;
  }

})();
